import { useEffect, useRef, useMemo } from 'react';
import { Direction_Enum } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { Bool_Enum } from '@weave/schema-gen-ts/dist/shared/null/types.pb';
import { FaxV1 } from '@frontend/api-fax';
import { GlobalSearch, useGlobalSearch } from '@frontend/global-search';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumberWithoutCountryCode } from '@frontend/phone-numbers';
import { useAppScopeStore, useOnScopeChange } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { InlineNotification, phone } from '@frontend/design-system';
import { useInfiniteScroll } from '../../hooks';
import { useFaxSearchFiltersShallowStore } from '../../stores';
import { FaxItemSkeletonLoader } from './fax-item-skeleton-loader';
import { FaxSearchResult } from './fax-search-result';

const DEFAULT_MAX_RESULTS = 4;
const DEFAULT_SEARCH_LIMIT = 10;
const FAX_SEARCH_LIMIT = 25;

export const FaxesGlobalSearchModule = () => {
  const { t } = useTranslation('fax');
  const { selectedLocationIds } = useAppScopeStore();

  const {
    faxSearchFilters: {
      tags: tagsFilter,
      faxNumbers: faxNumbersFilter,
      startDate: startDateFilter,
      endDate: endDateFilter,
      direction: directionFilter,
      isDelivered: isDeliveredFilter,
      isRead: isReadFilter,
    },
    resetFaxSearchFilters,
  } = useFaxSearchFiltersShallowStore('faxSearchFilters', 'resetFaxSearchFilters');

  useOnScopeChange(() => {
    resetFaxSearchFilters();
  });

  useEffect(() => {
    return () => {
      resetFaxSearchFilters();
    };
  }, []);

  const { debouncedSearchTerm, searchFor, viewAllFeature, setModuleData } = useGlobalSearch([
    'debouncedSearchTerm',
    'searchFor',
    'viewAllFeature',
    'setModuleData',
  ]);

  const containerRef = useRef<HTMLDivElement>(null);

  const containsNoAlphabet = (str: string) => !/[a-zA-Z]/.test(str);

  const conditionalSanitizePhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '';
    if (containsNoAlphabet(phoneNumber)) {
      return phone(phoneNumber, { format: 'numbers' });
    }
    return phoneNumber;
  };

  const searchRequest = useMemo(() => {
    const tagIds = tagsFilter?.length > 0 ? tagsFilter : undefined;

    const locationPhones =
      faxNumbersFilter?.length > 0 ? faxNumbersFilter.map(formatPhoneNumberWithoutCountryCode) : undefined;

    const isViewed = isReadFilter === 'read' ? Bool_Enum.TRUE : isReadFilter === 'unread' ? Bool_Enum.FALSE : undefined;

    const isDelivered =
      isDeliveredFilter === 'delivered'
        ? Bool_Enum.TRUE
        : isDeliveredFilter === 'undelivered'
        ? Bool_Enum.FALSE
        : undefined;

    const direction =
      directionFilter === 'inbound'
        ? Direction_Enum.INBOUND
        : directionFilter === 'outbound'
        ? Direction_Enum.OUTBOUND
        : undefined;

    const startAt = startDateFilter ? new Date(startDateFilter).toISOString() : undefined;
    const endAt = endDateFilter ? new Date(endDateFilter).toISOString() : undefined;

    return {
      locationIds: selectedLocationIds,
      query: conditionalSanitizePhoneNumber(debouncedSearchTerm),
      limit: searchFor === 'faxes' ? FAX_SEARCH_LIMIT : DEFAULT_SEARCH_LIMIT,
      includeArchived: true,
      tagIds,
      locationPhones,
      startAt,
      endAt,
      direction,
      isDelivered,
      isViewed,
    };
  }, [
    selectedLocationIds,
    debouncedSearchTerm,
    searchFor,
    tagsFilter,
    faxNumbersFilter,
    startDateFilter,
    endDateFilter,
    directionFilter,
    isDeliveredFilter,
    isReadFilter,
  ]);

  const {
    data: faxSearchData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = FaxV1.Queries.useSearchFaxesQuery({
    request: searchRequest,
    options: {
      enabled: debouncedSearchTerm.length > 1,
      retry: 2,
    },
  });

  const faxes = faxSearchData?.pages?.flatMap((page) => page?.rows?.filter(Boolean)) ?? [];
  const maxResults = viewAllFeature === 'faxes' ? faxes : faxes?.slice(0, DEFAULT_MAX_RESULTS);

  useEffect(() => {
    // TODO: Patch fix to prevent showing search empty state. Remove once this is fixed in GlobalSearch provider.
    if (isLoading) {
      setModuleData('faxes', true);
      return;
    }
    setModuleData('faxes', !!faxes.length);
  }, [debouncedSearchTerm, faxes.length, isLoading]);

  useInfiniteScroll({
    containerRef,
    hasNextPage: !!hasNextPage && viewAllFeature === 'faxes',
    isLoading,
    fetchNextPage,
    itemCount: maxResults.length,
    rootMargin: '0px',
  });

  return (
    <GlobalSearch.Section.Content
      showViewAll={faxes && faxes?.length > DEFAULT_MAX_RESULTS}
      hasResults={!!faxes?.length}
      count={!!faxes?.length && faxes.length > DEFAULT_SEARCH_LIMIT - 1 ? `${DEFAULT_SEARCH_LIMIT}+` : faxes?.length}
    >
      {!!maxResults.length ? (
        <InlineNotification
          message={t('Adjust filter to find faxes more than 13 months old.')}
          status='info'
          size='small'
          css={{ margin: theme.spacing(-0.5, 0, 1, 0) }}
        />
      ) : null}
      <div
        ref={containerRef}
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.spacing(1),
        }}
      >
        {isLoading ? (
          <FaxItemSkeletonLoader repeat={4} />
        ) : !!maxResults?.length ? (
          <>
            {maxResults?.map((faxItem) => (
              <FaxSearchResult key={faxItem.id} faxItem={faxItem} />
            ))}
            {isFetchingNextPage && <FaxItemSkeletonLoader repeat={4} />}
          </>
        ) : (
          <GlobalSearch.EmptyState
            customMessage={
              debouncedSearchTerm.length === 1 ? t('Type two or more characters to search faxes.') : undefined
            }
          />
        )}
      </div>
    </GlobalSearch.Section.Content>
  );
};
